import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Send, Ticket, CheckCircle2, ArrowRight, Mail, Shield } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrandedPortal() {
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", title: "", description: "" });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/api/portal/${productSlug}`);
        setProduct(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/portal/${productSlug}/tickets`, form);
      setTicketId(res.data.ticket_id);
      setSubmitted(true);
      toast.success("Ticket submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-lg text-slate-500">Loading...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Ticket className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Product Not Found</h2>
            <p className="text-slate-500">The support portal you're looking for doesn't exist or is no longer active.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const color = product?.primary_color || "#1a365d";

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)` }} data-testid="portal-success">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${color}15` }}>
              <CheckCircle2 className="h-10 w-10" style={{ color }} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Ticket Submitted</h2>
            <p className="text-slate-500 mb-4">
              Your support request has been received. Our team will get back to you shortly.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-500">Reference Number</p>
              <p className="font-mono font-bold text-lg" style={{ color }}>#{ticketId.slice(0, 8).toUpperCase()}</p>
            </div>
            <Button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", title: "", description: "" }); }}
              className="w-full" style={{ backgroundColor: color }}>
              Submit Another Ticket
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="branded-portal-page">
      {/* Branded Header */}
      <div className="py-6 px-6 border-b" style={{ backgroundColor: color }}>
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          {product.logo_url ? (
            <img src={product.logo_url} alt={product.name} className="h-10 w-10 rounded-lg object-contain bg-white/20 p-1" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold text-lg">
              {product.name.charAt(0)}
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold text-white">{product.name}</h1>
            <p className="text-white/70 text-sm">Support Portal</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8" style={{ background: `linear-gradient(180deg, ${color}08 0%, transparent 40%)` }}>
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="shadow-lg border-0">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Ticket className="h-5 w-5" style={{ color }} />
                    Submit a Support Request
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Your Name *</Label>
                        <Input placeholder="John Smith" value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="portal-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email Address *</Label>
                        <Input type="email" placeholder="john@company.com" value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="portal-email-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Subject *</Label>
                      <Input placeholder="Brief description of your issue" value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="portal-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Textarea placeholder="Please provide as much detail as possible about your issue..." rows={5}
                        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="portal-description-input" />
                    </div>
                    <Button type="submit" className="w-full h-12 text-white font-semibold"
                      style={{ backgroundColor: color }} disabled={submitting} data-testid="portal-submit-button">
                      {submitting ? "Submitting..." : <><Send className="h-4 w-4 mr-2" /> Submit Request</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar Info */}
            <div className="lg:col-span-2 space-y-6">
              {product.description && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-900 mb-2">About {product.name}</h3>
                    <p className="text-sm text-slate-600">{product.description}</p>
                  </CardContent>
                </Card>
              )}
              <Card className="border-0 shadow-md">
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-semibold text-slate-900">How It Works</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: color }}>1</div>
                      <p className="text-sm text-slate-600">Submit your support request using the form</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: color }}>2</div>
                      <p className="text-sm text-slate-600">Our team reviews and assigns a specialist</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: color }}>3</div>
                      <p className="text-sm text-slate-600">You'll receive updates via email</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {product.support_email && (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-900 mb-2">Email Support</h3>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4" style={{ color }} />
                      <a href={`mailto:${product.support_email}`} className="hover:underline" style={{ color }}>
                        {product.support_email}
                      </a>
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card className="border-0 shadow-md" style={{ backgroundColor: `${color}08` }}>
                <CardContent className="p-5 flex items-start gap-3">
                  <Shield className="h-5 w-5 shrink-0 mt-0.5" style={{ color }} />
                  <div>
                    <p className="text-sm font-medium text-slate-900">Secure & Confidential</p>
                    <p className="text-xs text-slate-500 mt-1">Your information is encrypted and handled with care by Sebastian Alliance Group.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 px-6 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">
          Powered by <span className="font-semibold">Sebastian Alliance Group</span> Helpdesk
        </p>
      </div>
    </div>
  );
}
